/**
 * 模組頁面代碼: 1210-UI
 * 模組說明: 機器資料頁面
 * @module MachineContainer
 */

// ----- import from local libraries -----
import {React} from '../../components/BaseComponent';

// ----- import from local components -----
import Header from '../../features/main-frame/components/Header';
import Footer from '../../features/main-frame/components/Footer';
import Sidebar from '../../features/main-frame/components/Sidebar';
import {useSelector, useDispatch} from 'react-redux';
import {Modal as BSModal} from 'bootstrap';
import MachinePage from './components/MachinePage';
import enumList from 'web/utils/enumList';
import {AlertModal} from 'web/components/modal/AlertModal';
import {useAlertModal} from 'web/components/modal/hooks/useAlertModal';
import {useRef, useState, useEffect} from 'react';
import {bulkEditMachines} from 'web/redux/actions/MachineActions';
import {showMessage} from 'web/utils/modalHelper';
import MachineModal, {DEFAULT_MACHINE_TYPE} from './components/MachineModal';
import AssignmentHistoryModal from './components/AssignmentHistoryModal';

// ----- constants and enums -----

const getEmptyMachineData = () =>
  JSON.parse(
    JSON.stringify({
      recordYear: '',
      deliveryCode: '',
      machineModel: '',
      machineSerialNumber: '',
      imCode: '',
      machineTypeId: DEFAULT_MACHINE_TYPE.id,
      flgStatus: enumList.flgStatus.active,
      memo: '',
    }),
  );

const MachineContainer = (props) => {
  const dispatch = useDispatch();
  const [machineModalMode, setMachineModalMode] = useState(
    enumList.modalMode.new,
  );

  const [needMachineLoad, setNeedMachineLoad] = useState(false);
  const [machineID, setMachineID] = useState(undefined);
  const [checkedMachineIds, setCheckedMachineIds] = useState([]);
  const [machineItem, realSetMachineItem] = useState(getEmptyMachineData());
  const setMachineItem = (...args) => {
    console.log(args);
    realSetMachineItem(...args);
  };

  const machineModalRef = useRef(null);
  const machineHistoryModalRef = useRef(null);
  const machineHistoryModalObjRef = useRef();
  const isBatchSelectCheckRef = useRef(false);
  const {
    alertRef,
    actionType,
    actionMode,
    actionIds,
    alertMessage,
    toggleAlertModal,
  } = useAlertModal();

  const setMachineModalRef = (element) => {
    if (element && !machineModalRef.current) {
      machineModalRef.current = new bootstrap.Modal(element);
    }
  };

  const handleEditMachineStatus = async (machineCodes, status) => {
    dispatch(
      bulkEditMachines({ids: machineCodes, options: {flgStatus: status}}),
    )
      .then((res) => {
        const statusCode = res.payload.code;
        if (statusCode && statusCode.isSuccessCode()) {
          const checkboxs = document.querySelectorAll('.machine-status-switch');
          checkboxs.forEach((checkbox) => {
            if (
              machineCodes.includes(parseInt(checkbox.dataset.id)) &&
              isBatchSelectCheckRef.current
            ) {
              checkbox.checked =
                status === enumList.flgStatus.active ? true : false;
            }
          });
          isBatchSelectCheckRef.current = false;
          showMessage(enumList.messageModalMode.info, '更新成功');
          toggleAlertModal(false);
          setCheckedMachineIds([]);
        } else {
          showMessage(enumList.messageModalMode.error, res);
          toggleAlertModal(false);
        }
      })
      .catch((err) => {
        showMessage(enumList.messageModalMode.error, err);
        console.error('updateMachineStatus error', err);
      });
  };

  const onConfirm = () => {
    const machineCodes = actionIds;
    console.log(machineCodes);
    if (
      [
        enumList.alertModalModeEnums.enable,
        enumList.alertModalModeEnums.disable,
      ].includes(actionMode)
    ) {
      handleEditMachineStatus(
        machineCodes,
        actionMode === enumList.alertModalModeEnums.enable
          ? enumList.flgStatus.active
          : enumList.flgStatus.inactive,
      );
    }
  };

  const toggleMachineAddModal = (isShow) => {
    setMachineModalMode(enumList.modalMode.new);
    setMachineItem(getEmptyMachineData());
    if (machineModalRef.current) {
      if (isShow) {
        machineModalRef.current.show();
      } else {
        machineModalRef.current.hide();
      }
    }
  };

  const toggleMachineEditModal = (isShow, machineItem) => {
    setMachineModalMode(enumList.modalMode.edit);
    setMachineItem(machineItem);
    if (machineModalRef.current) {
      if (isShow) {
        machineModalRef.current.show();
      } else {
        machineModalRef.current.hide();
      }
    }
  };

  const toggleMachineBindingHistoryModal = (isShow) => {
    if (machineHistoryModalObjRef.current) {
      if (isShow === true) {
        machineHistoryModalObjRef.current.show();
        return;
      }
      if (isShow === false) {
        machineHistoryModalObjRef.current.hide();
        return;
      }
      machineHistoryModalObjRef.current.toggle();
    }
  };

  useEffect(() => {
    if (!(machineHistoryModalRef.current instanceof HTMLDivElement)) {
      return;
    }
    machineHistoryModalObjRef.current = new BSModal(
      machineHistoryModalRef.current,
    );
    return () => {
      if (!(machineHistoryModalRef.current instanceof HTMLDivElement)) {
        return;
      }
      machineHistoryModalObjRef.current = undefined;
    };
  }, []);

  return (
    <>
      <div className="m__wrap">
        <Header />
        <div className="m__contents">
          <Sidebar />
          <MachinePage
            {...props}
            {...{checkedMachineIds, setCheckedMachineIds}}
            setMachineID={setMachineID}
            toggleMachineAddModal={toggleMachineAddModal}
            toggleMachineEditModal={toggleMachineEditModal}
            onBatchHandle={(
              mode,
              message,
              machineCodes,
              isBatchSelect = false,
            ) => {
              if (isBatchSelect) isBatchSelectCheckRef.current = true;
              toggleAlertModal(
                true,
                mode,
                enumList.targetAction.machine,
                message,
                machineCodes,
              );
            }}
            needMachineLoad={needMachineLoad}
            setNeedMachineLoad={setNeedMachineLoad}
            {...{toggleMachineBindingHistoryModal, setMachineID}}
          />
        </div>
        <Footer />
      </div>
      <AlertModal
        ref={alertRef}
        mode={actionMode}
        message={alertMessage}
        onConfirm={onConfirm}
        onCancel={() => {
          if (isBatchSelectCheckRef.current) {
            return (isBatchSelectCheckRef.current = false);
          }
          const checkebox = document.querySelector(
            `[data-id="${actionIds[0]}"]`,
          );
          if (checkebox) {
            checkebox.checked = !checkebox.checked;
          }
        }}
      />
      <MachineModal
        {...props}
        ref={setMachineModalRef}
        mode={machineModalMode}
        machineItem={machineItem}
        toggleMachineAddModal={toggleMachineAddModal}
        toggleMachineEditModal={toggleMachineEditModal}
        reloadDepartmentList={() => {
          setNeedMachineLoad(true);
        }}
        onErrorHandle={(title, message) =>
          showMessage(true, enumList.messageModalMode.error, title, message)
        }
      />
      <AssignmentHistoryModal
        {...props}
        ref={machineHistoryModalRef}
        machineID={machineID}
        setMachineID={setMachineID}
        onErrorHandle={(title, message) =>
          showMessage(true, enumList.messageModalMode.error, title, message)
        }
      />
    </>
  );
};

export default MachineContainer;
